import React, { useState, useCallback } from 'react';
import {
  Button,
  HeadingText,
  ParagraphText,
  queryHelpers,
  StackItem,
  StackLayout,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import {
  ANALYTICS_SCREEN_NAMES,
  DETAIL,
  FullScreenContainer,
  LoadingSpinner,
  PRODUCT_AREA_NAMES,
  SUB_PRODUCT_AREA_NAMES,
} from '@telus-alberta-health-web/shared-module';
import { TOAST_STATUS, toastActions } from '@leagueplatform/toast-messages';
import { useIntl } from '@leagueplatform/locales';
import { OnboardingFlowContainer } from 'components/onboarding-flow-container/onboarding-flow-container';
import ActionCompleteImage from 'assets/action-complete-image.svg';
import { FEATURE_FLAGS, SECONDARY_CONTENT_MAX_WIDTH } from 'common/constants';
import { useNavigate } from 'react-router-dom';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { useFeatureFlagQuery } from '@leagueplatform/user-profile-api';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import {
  consumePostLoginRedirect,
  toSafeInternalPath,
} from 'utils/post-login-redirect';
import {
  EVENT_NAME,
  trackAnalyticsEvent,
  usePageViewAnalytics,
} from '@leagueplatform/analytics';

interface OnboardingCompleteProps {
  onComplete: () => Promise<void>;
}

export const OnboardingComplete: React.FC<OnboardingCompleteProps> = ({
  onComplete,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(queryHelpers.down('tablet'));

  const { data: isRedesignEnabled } = useFeatureFlagQuery(
    FEATURE_FLAGS.MY_HEALTH_REDESIGN_ENABLED,
  );
  // Treat loading (undefined) as off so the original copy shows without a flash.
  const showRedesign = isRedesignEnabled === true;

  const [isSubmitting, setIsSubmitting] = useState(false);

  usePageViewAnalytics({
    product_area: PRODUCT_AREA_NAMES.FOUNDATION,
    sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
    screen_name: ANALYTICS_SCREEN_NAMES.COMPLETE_ONBOARDING,
  });

  const handleClick = useCallback(
    async (navigateToHome: boolean) => {
      setIsSubmitting(true);
      try {
        if (navigateToHome) {
          trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
            product_area: PRODUCT_AREA_NAMES.FOUNDATION,
            sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
            screen_name: ANALYTICS_SCREEN_NAMES.COMPLETE_ONBOARDING,
            detail: DETAIL.NAVIGATE_TO_HOME,
          });
        } else {
          trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
            product_area: PRODUCT_AREA_NAMES.FOUNDATION,
            sub_product_area: SUB_PRODUCT_AREA_NAMES.ONBOARDING,
            screen_name: ANALYTICS_SCREEN_NAMES.COMPLETE_ONBOARDING,
            detail: showRedesign
              ? DETAIL.NAVIGATE_TO_MY_HEALTH
              : DETAIL.NAVIGATE_TO_SERVICES,
          });
        }

        await onComplete();
        // Onboarding replaces the private routes, so the landing page never
        // mounts to restore a deep link attempted before login (e.g. /chat).
        // Consume it here: it stands in for Home, but an explicit Services
        // choice still wins, and the value is cleared either way.
        const pendingRedirect = consumePostLoginRedirect();
        const explicitChoice = showRedesign
          ? getModulePath(LEAGUE_MODULE_NAMES.aboutMe)
          : APP_MANAGED_ROUTES.AUTHENTICATED_CARE_SERVICES;
        navigate(
          navigateToHome ? toSafeInternalPath(pendingRedirect) : explicitChoice,
        );
      } catch (error) {
        toastActions.add({
          type: TOAST_STATUS.ERROR,
          text: formatMessage({ id: 'STR_ERROR_STATE_BODY_API' }),
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, onComplete, formatMessage, showRedesign],
  );

  return (
    <FullScreenContainer
      css={{
        background: '$surfaceBackgroundSecondary',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <OnboardingFlowContainer
        css={{
          maxWidth: SECONDARY_CONTENT_MAX_WIDTH,
          width: '100%',
          margin: 'auto',
        }}
        isMobile={isMobile}
      >
        <StackLayout
          horizontalAlignment="center"
          verticalAlignment="center"
          css={{
            maxWidth: '468px',
            margin: 'auto',
          }}
        >
          <img
            src={ActionCompleteImage}
            height="176px"
            width="176px"
            alt={formatMessage({
              id: 'STR_ONBOARDING_COMPLETE_TITLE',
            })}
          />
          <StackItem
            horizontalAlignment="center"
            css={{
              marginBlock: '$twoAndHalf',
              textAlign: 'center',
              gap: '$half',
            }}
          >
            <HeadingText level="3" size="lg">
              {formatMessage({
                id: 'STR_ONBOARDING_COMPLETE_TITLE',
              })}
            </HeadingText>
            <ParagraphText
              css={{
                typography: '$bodyOne',
                color: '$onSurfaceTextSubdued',
                '&&': {
                  marginBlockEnd: '$half',
                  '@mobile': {
                    marginBlockEnd: '$threeQuarters',
                  },
                },
              }}
            >
              {formatMessage({
                id: 'STR_ONBOARDING_COMPLETE_DESCRIPTION',
              })}
            </ParagraphText>
            <ParagraphText
              css={{
                typography: '$bodyOne',
                color: '$onSurfaceTextSubdued',
              }}
            >
              {formatMessage({
                id: showRedesign
                  ? 'STR_ONBOARDING_COMPLETE_DESCRIPTION_SECONDARY'
                  : 'STR_ONBOARDING_COMPLETE_DESCRIPTION_SECONDARY_SERVICES',
              })}
            </ParagraphText>
          </StackItem>
          {isSubmitting ? (
            <StackItem css={{ height: '124px', position: 'relative' }}>
              <LoadingSpinner />
            </StackItem>
          ) : (
            <StackItem css={{ gap: '$oneAndHalf', width: '100%' }}>
              <Button width="fillContainer" onClick={() => handleClick(true)}>
                {formatMessage({ id: 'STR_START_EXPLORING' })}
              </Button>
              <Button
                width="fillContainer"
                priority="tertiary"
                onClick={() => handleClick(false)}
              >
                {formatMessage({
                  id: showRedesign
                    ? 'STR_TAKE_ME_TO_MY_HEALTH'
                    : 'STR_TAKE_ME_TO_SERVICES',
                })}
              </Button>
            </StackItem>
          )}
        </StackLayout>
      </OnboardingFlowContainer>
    </FullScreenContainer>
  );
};
